import type { SectionLayoutCoverageIssues } from '../../components/oasis/bespoke/layout/validation/validateSectionLayout';

/**
 * User message sent when requesting an LLM pass to repair coverage gaps.
 *
 * The standing instructions below are authored; the findings are not. Rendering
 * findings from the issue list is what keeps the assistant from being asked to
 * satisfy a rule nobody told it about — this function used to restate all six
 * issue kinds in its own words, and a seventh would have been invisible here
 * while still blocking the save.
 */
export function buildCoverageFixAssistantPrompt(
  coverage: SectionLayoutCoverageIssues
): string {
  const lines: string[] = [
    'Fix the Roger Layout Schema for this section so coverage validation passes.',
    '',
    'Update the layout JSON to:',
    '- Add a proper answer control for every required schema QID that is still missing from the layout.',
    '- Remove or replace references to QIDs that are not part of this section schema (only use QIDs from the bundled section schema).',
    '- For radio questions rendered as tables with `radio-option` rows: each row code must match the schema values[] for that QID exactly—add missing codes and remove extras.',
    '- Do not hide required answer controls with CSS (`hidden`, `invisible`, `opacity-0`, `display: none`, `visibility: hidden`, `opacity: 0`) on field nodes or ancestor wrappers.',
    '- Top-level schema questions must render outside `conditional` and `disabled-when` blocks. Nested subitem questions may sit inside those gates optionally; when gated, `{ qid, eq }` / `{ qid, includes }` clauses may only reference schema ancestors (parent or higher) with the schema subitems answer key. **`conditional.when` = show when true; `disabled-when.whenAll` = keep enabled while all clauses hold (disable when any fail). Use the same schema-path answer for both — do not invert the eq value.** Never wrap the gate control itself inside its own `disabled-when`.',
    '- For each lettered CMS item with layout controls: the lettered base id (e.g. `M0020`, `GG0100`) must appear in user-visible layout text (static, section-label, table header/row label, field labelOverride, or gated-row top static)—prefer parentheses, e.g. `(M0020) Medical Record Number`. Do not use cms_casting suffixes (`GG0100A`, `GG0100B`) on labels.',
    '- Avoid redundant copy: do not repeat a group heading phrase in every child option label (e.g. heading **Oriented to:** with options **Person** / **Place** / **Time**, not **Oriented to person** again).',
    '',
    'Reply with a corrected complete layout_json array.',
    '',
    'Current validation findings:',
  ];

  if (!coverage.parseOk) {
    lines.push('', 'The current layout JSON could not be parsed or validated.');
  }

  for (const issue of coverage.issues) {
    const items = issue.promptItems ?? issue.items;
    lines.push(
      '',
      `${issue.title} (${issue.count})${issue.promptHint ?? ''}:`,
      // Bare QIDs read better as one comma list than as dozens of bullets;
      // per-item sentences need the bullets. Same split the editor panel uses.
      ...(issue.display === 'inline'
        ? [items.join(', ')]
        : items.map(item => `- ${item}`))
    );
  }

  return lines.join('\n');
}

/** Compact JSON for vibecoder API payloads; editor UI may keep pretty-printed text. */
export function compactJsonTextForVibecoderApi(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text));
  } catch {
    return text;
  }
}

export function layoutJsonTextForCoverageFix(
  layoutJson: unknown | null | undefined
): string {
  if (layoutJson == null) return '[]';
  try {
    return JSON.stringify(layoutJson);
  } catch {
    return '[]';
  }
}
